/**
 * Phase breakdowns per medium — used by PhaseAccumulator
 * to let users build up hours from typical workflow stages.
 */

import type { MediumKey } from '@/types';
import type { TierKey } from '@/types/tier';

/**
 * Size scale for hour estimates — continuous power curve.
 * Reference: 500 sq in → 1.0×. Clamped to [0.3, 3.0].
 */
export function getSizeScale(area: number): number {
  if (area <= 0) return 1.0;
  const raw = Math.pow(area / 500, 0.6);
  return Math.max(0.3, Math.min(raw, 3.0));
}

/**
 * Fidelity scale for hour estimates.
 * L3 (Commission) = 1.0× reference — base hours unchanged.
 */
export const FIDELITY_SCALE: Record<TierKey, number> = {
  L1: 0.50,
  L2: 0.75,
  L3: 1.00,
  L4: 1.30,
  L5: 1.75,
};

/**
 * Base hours per medium (sum of all phase items at 500 sq in / L3).
 * Derived from PHASES — do not change independently.
 */
export const BASE_HOURS: Record<MediumKey, number> = {
  oil: 21,
  acrylic: 14,
  watercolor: 11,
  gouache: 11,
  pastel: 11,
  mixed_media: 17,
};

/**
 * Estimate hours for a piece based on medium, area, and fidelity tier.
 * Formula: baseHours × sizeScale × fidelityScale, rounded to nearest integer.
 * Graceful fallbacks: missing area → 500 sq in, missing tier → L3.
 */
export function estimateHours(
  medium: MediumKey,
  area?: number,
  tier?: TierKey,
): number {
  const base = BASE_HOURS[medium];
  const sizeScale = getSizeScale(area && area > 0 ? area : 500);
  const fidelityScale = FIDELITY_SCALE[tier ?? 'L3'];
  return Math.round(base * sizeScale * fidelityScale);
}

export interface PhaseItem {
  label: string;
  hrs: number;
}

export const PHASES: Record<MediumKey, PhaseItem[]> = {
  oil: [
    { label: 'Sketch', hrs: 2 },
    { label: 'Underpainting', hrs: 3 },
    { label: 'Block-in', hrs: 4 },
    { label: 'Rendering', hrs: 7 },
    { label: 'Detail', hrs: 4 },
    { label: 'Varnish', hrs: 1 },
  ],
  acrylic: [
    { label: 'Sketch', hrs: 2 },
    { label: 'Base', hrs: 3 },
    { label: 'Layers', hrs: 5 },
    { label: 'Detail', hrs: 3 },
    { label: 'Finish', hrs: 1 },
  ],
  watercolor: [
    { label: 'Sketch', hrs: 1 },
    { label: 'First wash', hrs: 2 },
    { label: 'Layering', hrs: 4 },
    { label: 'Detail', hrs: 3 },
    { label: 'Final wash', hrs: 1 },
  ],
  gouache: [
    { label: 'Sketch', hrs: 1 },
    { label: 'Base coat', hrs: 2 },
    { label: 'Layering', hrs: 4 },
    { label: 'Detail', hrs: 3 },
    { label: 'Touch-up', hrs: 1 },
  ],
  pastel: [
    { label: 'Sketch', hrs: 1 },
    { label: 'Underpainting', hrs: 2 },
    { label: 'Layering', hrs: 4 },
    { label: 'Blending', hrs: 3 },
    { label: 'Fixative', hrs: 1 },
  ],
  mixed_media: [
    { label: 'Sketch', hrs: 2 },
    { label: 'Base layer', hrs: 3 },
    { label: 'Primary medium', hrs: 5 },
    { label: 'Secondary medium', hrs: 4 },
    { label: 'Integration', hrs: 2 },
    { label: 'Finish', hrs: 1 },
  ],
};
